/**
 * Golden demo fixtures for VC demos and investor presentations.
 *
 * These provide deterministic, impressive responses so the platform
 * always looks spectacular during live demos — no API calls needed.
 */

// ─── Strategy Studio Demo ───────────────────────────────────────────────────

export const DEMO_STRATEGY_ANALYSIS = {
  content: `# Competitive Positioning in Enterprise AI Market

## Executive Summary

The enterprise AI market is undergoing a fundamental structural shift. With projected growth from **$62B (2024) to $310B by 2029** (CAGR 38.1%), the competitive landscape is being reshaped by three converging forces: the commoditization of foundation models, the rise of domain-specific AI platforms, and increasing enterprise demand for explainable, auditable AI systems.

---

## Market Sizing & Segmentation

| Segment | 2024 Revenue | 2029 Projected | CAGR |
|---------|-------------|----------------|------|
| Generative AI Platforms | $18.2B | $97.4B | 39.8% |
| ML Ops / AI Infrastructure | $12.8B | $52.1B | 32.4% |
| Vertical AI Solutions | $11.4B | $68.3B | 43.1% |
| AI Analytics & BI | $9.6B | $42.7B | 34.8% |
| Conversational AI | $7.2B | $35.6B | 37.6% |
| AI Security & Governance | $2.8B | $13.9B | 37.9% |

**Key Insight:** Vertical AI solutions represent the fastest-growing segment, validating the strategy of domain-specific intelligence platforms over horizontal tooling.

---

## Competitive Landscape

### Tier 1: Hyperscalers (Market Makers)
- **Microsoft/OpenAI** — Dominant distribution through Azure + M365 integration. Risk: enterprise lock-in fatigue
- **Google DeepMind** — Strongest research pipeline (Gemini). Weakness: enterprise GTM execution
- **AWS/Amazon** — Broadest infrastructure play (Bedrock). Weakness: fragmented product narrative

### Tier 2: Enterprise AI Specialists
- **Palantir** — Government-grade AI ops (AIP). Strength: mission-critical trust
- **Databricks** — Data lakehouse + AI convergence. Weakness: analytics-heavy, not strategy-native
- **Snowflake** — Cortex AI push, strong data gravity. Weakness: late AI entrant

### Tier 3: Strategic Intelligence (Direct Competitors)
- **AlphaSense** — Financial intelligence leader, $4B+ valuation
- **Crayon** — Competitive intelligence with CI automation
- **Klue** — Win/loss analysis and battlecard automation

---

## Strategic Recommendations

### 1. Differentiate on "Intelligence Architecture"
Rather than competing on model quality (a losing game against hyperscalers), position around the **intelligence architecture**: the system that orchestrates multiple AI capabilities into actionable strategic outputs. This is defensible because:
- It requires deep domain expertise in strategic frameworks
- The quality of outputs improves with organization-specific data flywheel
- Switching costs increase with historical analysis depth

### 2. Build the Bayesian Moat
The platform's probabilistic reasoning engine (Bayesian posterior updates, FWER-controlled hypothesis testing, bootstrap confidence intervals) represents genuine IP that consultancies cannot replicate with off-the-shelf tools. **Recommendation: file provisional patents on the graphical testing methodology.**

### 3. Land-and-Expand via Strategy Studio
Strategy Studio's time-to-insight advantage (4-minute analysis vs. 2-week consulting engagement) creates a natural expansion path:
- **Land:** Individual strategist adoption (freemium)
- **Expand:** Team deployment with shared knowledge graphs
- **Enterprise:** Organization-wide intelligence platform with API access

### 4. Counter-Position Against Traditional Consulting
The $300B+ management consulting market is vulnerable to AI disruption. Position explicitly against the "6-week strategy project" model:

| Metric | Traditional Consulting | This Platform |
|--------|----------------------|---------------|
| Time to First Insight | 2-6 weeks | 4 minutes |
| Cost per Strategic Analysis | $150K-$500K | $299/month |
| Data Freshness | Point-in-time snapshot | Real-time continuous |
| Analyst Bias | High (confirmation bias) | Bayesian-calibrated |
| Scalability | Linear (headcount) | Exponential (compute) |

### 5. Pursue SOC 2 Type II + FedRAMP
Enterprise buyers increasingly require security certifications. Achieving SOC 2 Type II within 6 months and initiating FedRAMP moderate would unlock:
- Federal/defense market (~$8B TAM)
- Fortune 500 procurement requirements
- Insurance and financial services verticals

---

## Risk Factors

1. **Foundation model commoditization** could erode AI differentiation — mitigated by focusing on orchestration layer
2. **Enterprise sales cycle length** (6-12 months) requires sufficient runway — mitigated by PLG freemium motion
3. **Data privacy concerns** with AI analysis of sensitive strategic data — mitigated by on-premise deployment option
4. **Talent competition** for ML engineers — mitigated by Deno/edge architecture reducing infrastructure complexity

---

## Conclusion

The enterprise AI market presents a once-in-a-generation opportunity for platforms that can bridge the gap between raw AI capability and strategic business intelligence. The key differentiator is not the AI model itself, but the **intelligence architecture** that transforms data into defensible strategic insights. Companies that establish this position in the next 18 months will benefit from compounding data network effects that create durable competitive advantages.

*Analysis confidence: 87% | Sources: 8 verified | Generated in 3.8 minutes*`,

  sources: [
    { title: 'Gartner Magic Quadrant for AI Platforms 2024', url: 'https://gartner.com/reviews/market/ai-platforms', confidence: 0.94 },
    { title: 'McKinsey Global Institute: The State of AI 2024', url: 'https://mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai', confidence: 0.91 },
    { title: 'IDC Worldwide AI Spending Guide', url: 'https://idc.com/getdoc.jsp?containerId=AI', confidence: 0.89 },
    { title: 'CB Insights: Enterprise AI Market Map 2024', url: 'https://cbinsights.com/research/enterprise-ai-market-map', confidence: 0.87 },
    { title: 'Forrester Wave: AI Strategy Consulting Q3 2024', url: 'https://forrester.com/report/ai-strategy-consulting', confidence: 0.86 },
    { title: 'SEC Filing: Palantir 10-K Annual Report', url: 'https://sec.gov/cgi-bin/browse-edgar?company=palantir', confidence: 0.95 },
    { title: 'Bloomberg Intelligence: AI Software Revenue Forecast', url: 'https://bloomberg.com/professional/insights/ai-software', confidence: 0.88 },
    { title: 'Harvard Business Review: AI Disruption of Consulting', url: 'https://hbr.org/2024/ai-disruption-management-consulting', confidence: 0.85 },
  ],

  aggregateConfidence: 0.87,
  frameworksDetected: ['Porter\'s Five Forces', 'TAM/SAM/SOM', 'SWOT', 'Value Chain'],
  generationTimeMs: 228000,
};

// ─── Bayesian Diagnostic Demo ───────────────────────────────────────────────

export const DEMO_DIAGNOSTIC_SESSION = {
  id: 'demo-session-001',
  problemStatement: 'Enterprise sales conversion rates have declined 23% over the past two quarters despite increased pipeline volume',
  industry: 'B2B SaaS',
  status: 'converged' as const,

  hypotheses: [
    {
      id: 'h1',
      statement: 'Pricing misalignment with target market segment — enterprise tier pricing exceeds perceived value relative to alternatives',
      category: 'pricing',
      prior_probability: 0.20,
      current_posterior: 0.67,
      reasoning: 'Strong evidence from lost deal analysis showing 64% cite pricing as primary objection, supported by competitor benchmarking data',
      supporting_indicators: ['Lost deal surveys cite pricing 64% of time', 'Competitor undercutting by 30-40%', 'Win rate drops dramatically above $100K ACV'],
      refuting_indicators: ['Net-new pipeline volume is increasing', 'Expansion revenue from existing customers is stable'],
      status: 'leading' as const,
      position: 1,
    },
    {
      id: 'h2',
      statement: 'Product-market fit erosion — feature gaps relative to competitors creating evaluation failures in late-stage deals',
      category: 'product',
      prior_probability: 0.20,
      current_posterior: 0.15,
      reasoning: 'Some evidence of feature gap complaints but not consistent across lost deals',
      supporting_indicators: ['3 major feature requests in competitive losses', 'G2 comparison page shows gaps in 2 categories'],
      refuting_indicators: ['Product NPS remains at 72', 'Feature usage analytics show strong engagement', 'Analyst coverage is positive'],
      status: 'active' as const,
      position: 2,
    },
    {
      id: 'h3',
      statement: 'Sales execution degradation — new reps hired in Q2 expansion have insufficient training on enterprise selling methodology',
      category: 'execution',
      prior_probability: 0.20,
      current_posterior: 0.10,
      reasoning: 'Some correlation with new rep ramp but veteran reps also showing decline',
      supporting_indicators: ['60% of new reps below quota', 'Average ramp time increased to 8 months'],
      refuting_indicators: ['Veteran rep conversion also declined 12%', 'Sales enablement satisfaction survey at 7.2/10'],
      status: 'active' as const,
      position: 3,
    },
    {
      id: 'h4',
      statement: 'Market timing — macroeconomic headwinds causing enterprise budget freezes and extended procurement cycles',
      category: 'macro',
      prior_probability: 0.20,
      current_posterior: 0.06,
      reasoning: 'Budget freeze narrative weakened by evidence that total IT spending is flat, not declining',
      supporting_indicators: ['Average sales cycle extended by 3 weeks', 'CFO involvement in deals increased 40%'],
      refuting_indicators: ['Gartner IT spending forecast is flat YoY', 'Competitors reporting similar or higher growth', 'Pipeline volume is up 18%'],
      status: 'active' as const,
      position: 4,
    },
    {
      id: 'h5',
      statement: 'Channel conflict — partner-sourced deals cannibalizing direct sales without proper attribution',
      category: 'operations',
      prior_probability: 0.20,
      current_posterior: 0.02,
      reasoning: 'Eliminated — channel represents only 8% of pipeline and attribution rules were updated in Q1',
      supporting_indicators: ['Partner deal volume up 45%'],
      refuting_indicators: ['Channel is only 8% of total pipeline', 'Attribution rules updated Q1', 'No overlap in CRM pipeline analysis'],
      status: 'eliminated' as const,
      position: 5,
    },
  ],

  evidenceItems: [
    { id: 'e1', question_text: 'What percentage of lost deals cite pricing as the primary objection?', user_response: '64% based on our loss analysis surveys from the last two quarters', impact_summary: 'Strong support for pricing hypothesis (h1), LR=4.2' },
    { id: 'e2', question_text: 'How does your pricing compare to the top 3 competitors in your segment?', user_response: 'We are 30-40% above the closest competitor for similar enterprise tiers', impact_summary: 'Reinforces pricing hypothesis, mild evidence against product-market fit (h2)' },
    { id: 'e3', question_text: 'Has your product Net Promoter Score changed in the last 6 months?', user_response: 'NPS has been stable at 72, actually up slightly from 69', impact_summary: 'Contradicts product-market fit erosion (h2), neutral for pricing (h1)' },
    { id: 'e4', question_text: 'What is the conversion rate for deals under $100K ACV vs. over $100K ACV?', user_response: 'Under 100K: 28% (slightly up). Over 100K: 12% (down from 22%)', impact_summary: 'Very strong evidence for pricing sensitivity at enterprise tier, LR=6.8 for h1' },
    { id: 'e5', question_text: 'Are veteran sales reps (>1 year tenure) also experiencing conversion decline?', user_response: 'Yes, veteran reps are down about 12%, though new reps are down 35%', impact_summary: 'Weakens sales execution hypothesis (h3) — not purely a training issue' },
    { id: 'e6', question_text: 'Has the competitive landscape changed in the last two quarters?', user_response: 'Two new entrants launched lower-cost alternatives targeting our mid-market segment', impact_summary: 'Supports pricing pressure narrative, LR=3.1 for h1' },
    { id: 'e7', question_text: 'What does your win/loss data show about deal stage where most losses occur?', user_response: 'Most losses happen at the procurement/negotiation stage, not during evaluation', impact_summary: 'Strong evidence for pricing (h1), contradicts product-market fit (h2)' },
    { id: 'e8', question_text: 'Is total IT spending in your target market declining or flat?', user_response: 'Gartner reports flat IT spending YoY, and two competitors reported growth last quarter', impact_summary: 'Eliminates macro budget freeze hypothesis (h4)' },
  ],

  convergenceState: {
    is_converged: true,
    leading_hypothesis_id: 'h1',
    leading_probability: 0.67,
    entropy: 0.42,
    questions_asked: 8,
    estimated_remaining: 0,
  },

  bootstrapCI: { lower: 0.52, upper: 0.78 },
  watchlist: ['h2', 'h3'],

  conclusionReport: `## Root Cause Analysis: Declining Enterprise Sales Conversion

### Primary Finding
**Pricing misalignment with target market segment** has been identified as the root cause with **67% confidence** (95% CI: [52%, 78%]).

### Evidence Chain
1. 64% of lost deals explicitly cite pricing as primary objection
2. Enterprise tier ($100K+ ACV) conversion dropped from 22% to 12%, while sub-$100K remained stable
3. Two new market entrants offering 30-40% lower pricing captured mid-market segment
4. Win/loss analysis shows losses concentrated at procurement stage, not evaluation
5. Product satisfaction (NPS 72) and pipeline volume (+18%) remain healthy

### Recommended Strategic Actions
1. **Immediate:** Launch a competitive pricing analysis and develop a mid-market pricing tier
2. **Short-term (30 days):** A/B test value-based pricing vs. feature-based pricing for enterprise tier
3. **Medium-term (90 days):** Develop ROI calculator to justify premium pricing with quantified value
4. **Ongoing:** Monitor competitor pricing quarterly using automated competitive intelligence

### Recommended Frameworks
- Porter's Five Forces (competitive intensity analysis)
- Value-Based Pricing Framework
- Jobs-to-be-Done (buyer value perception)

### Watchlist Hypotheses
- **Product-market fit (15%):** Continue monitoring — if pricing adjustment doesn't restore conversion, revisit
- **Sales execution (10%):** New rep ramp time warrants attention regardless of pricing fix`,
};

// ─── Deal Room Demo ─────────────────────────────────────────────────────────

export const DEMO_DEAL_ROOM = {
  company: {
    name: 'TechVenture AI',
    stage: 'Series B',
    sector: 'Enterprise AI / MLOps',
    founded: '2021',
    hq: 'San Francisco, CA',
    employees: 127,
    lastRound: '$18M Series A (2023)',
    raising: '$45M Series B',
    preMoneyValuation: '$180M',
  },

  metrics: {
    arr: '$12.4M',
    arrGrowth: '215% YoY',
    nrr: '142%',
    grossMargin: '82%',
    ltv_cac: '4.2x',
    paybackMonths: 11,
    customers: 89,
    enterpriseCustomers: 23,
    magic_number: 1.4,
    burn_multiple: 1.8,
  },

  icMemoSections: {
    investmentThesis: 'TechVenture AI is building the intelligence layer for enterprise AI deployment. Their Bayesian orchestration engine has demonstrated 3x faster time-to-production for ML models vs. incumbent solutions, with a growing moat from organization-specific training data.',
    marketOpportunity: 'The MLOps market is projected to grow from $4.2B (2024) to $18.7B (2029). TechVenture targets the underserved mid-market segment ($10M-$500M revenue companies) that cannot afford custom ML infrastructure.',
    competitiveLandscape: 'Primary competitors include Weights & Biases (developer-focused), MLflow (open-source), and Databricks MLOps (enterprise). TechVenture differentiates through vertical-specific templates and no-code deployment.',
    risks: [
      'Hyperscaler bundling risk — AWS/Azure could integrate similar capabilities',
      'Key-person risk — 2 of 3 co-founders are critical to ML architecture',
      'Enterprise sales cycle may be longer than projected',
      'Open-source alternatives could commoditize core features',
    ],
    recommendation: 'INVEST — Lead $45M Series B at $180M pre. Strong unit economics, defensible technology moat, and expanding market.',
  },
};

// ─── Master Demo Data Object ────────────────────────────────────────────────

export const DEMO_DATA = {
  strategyAnalysis: DEMO_STRATEGY_ANALYSIS,
  diagnosticSession: DEMO_DIAGNOSTIC_SESSION,
  dealRoom: DEMO_DEAL_ROOM,
} as const;
